import React, { useState } from 'react';
import '../assets/styles/ChatSidebar.css';

const ChatSidebar = ({
  contacts,
  userNames = {},
  selectedContact,
  onSelectContact,
  loading = false,
  refreshing = false,
  onRefresh,
  activeTab = 'all',
  onTabChange,
  onEndChat,
  myStaffId = null,
  isStaff = false,
  onClaimChat,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Helper functions
  const getDisplayName = (contact, userInfo = {}) => {
    // Use user_details.name from API response if available
    if (contact.user_details?.name) {
      return contact.user_details.name;
    }

    // Fallback to userInfo if provided
    if (userInfo.name) {
      return userInfo.name;
    }

    // Fallback to contact direct properties
    return contact.full_name ||
           (contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : null) ||
           contact.first_name ||
           contact.last_name ||
           contact.name ||
           contact.user_type?.charAt(0).toUpperCase() + contact.user_type?.slice(1) ||
           'User';
  };

  const getUserColor = (type) => {
    switch (type) {
      case 'builder': return '#E6F4EA';
      case 'agent': return '#E8F0FE';
      case 'agency': return '#FEF7E0';
      default: return '#F1F3F4';
    }
  };

  // Tabs matching React Native app
  const tabs = [
    { id: 'all', label: 'All', type: null },
    { id: 'builder', label: 'Builders', type: 'builder' },
    { id: 'agent', label: 'Agents', type: 'agent' },
    { id: 'agency', label: 'Agencies', type: 'agency' },
  ];

  // Filter contacts based on search query and active tab
  const filteredContacts = contacts.filter((contact) => {
    const userInfo = userNames[contact.user_id] || {};
    const displayName = getDisplayName(contact, userInfo);

    const matchesSearch = displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.user_details?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         userInfo.name?.toLowerCase().includes(searchQuery.toLowerCase());

    if (activeTab === 'all') return matchesSearch;
    return matchesSearch && contact.user_type === activeTab;
  });

  const handleTabPress = (tab) => {
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const getDisplayType = (contact) => {
    // Use user_details.role if available, otherwise fallback to user_type
    let displayType = contact.user_details?.role || contact.user_type || 'User';

    if (displayType === 'sub_user') {
      displayType = contact.role === 'agency' || contact.parent_role === 'agency' ? 'agency' :
                   contact.role === 'builder' || contact.parent_role === 'builder' ? 'builder' :
                   contact.role === 'agent' || contact.parent_role === 'agent' ? 'agent' : 'team_member';
    }
    return displayType.charAt(0).toUpperCase() + displayType.slice(1);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return 'Yesterday';
    }
  };

  const isOnline = (contact) => {
    // Check online status from user_details if available
    if (contact.user_details?.online_status) {
      return contact.user_details.online_status === 'available' || contact.user_details.online_status === 'busy';
    }
    return contact.online_status === 'available' || contact.online_status === 'busy';
  };

  return (
    <div className="chat-sidebar-wrapper">
      <div className="sidebar-header-fixed">
        <h2 className="sidebar-title">Messages</h2>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabPress(tab)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="search-container-fixed">
        <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2"/>
          <path d="M21 21l-4.35-4.35" stroke="#999" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          className="search-input"
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="contacts-list-scrollable">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
          </div>
        ) : filteredContacts.length > 0 ? (
          filteredContacts.map((contact) => {
            const userInfo = userNames[contact.user_id] || {};
            const displayName = getDisplayName(contact, userInfo);
            const displayType = getDisplayType(contact);
            const contactIsOnline = isOnline(contact);

            return (
              <div
                key={contact.id}
                className={`contact-item ${selectedContact?.id === contact.id ? 'active' : ''}`}
                onClick={() => {
                  console.log('🖱️ ChatSidebar: Contact clicked:', contact.id, contact);
                  onSelectContact(contact);
                }}
              >
                <div className="contact-avatar-wrapper">
                  <div
                    className="contact-avatar"
                    style={{ backgroundColor: getUserColor(contact.user_type) }}
                  >
                    {contact.user_details?.name?.charAt(0) || displayName.charAt(0) || displayType.charAt(0)}
                  </div>
                  {contactIsOnline && <span className="online-indicator"></span>}
                </div>
                <div className="contact-info">
                  <div className="contact-top">
                    <div className="name-type-container">
                      <h3 className="contact-name">{displayName}</h3>
                      <span className="contact-type">({displayType})</span>
                    </div>
                    <span className="contact-time">
                      {formatTime(contact.last_message_at)}
                    </span>
                  </div>
                  <div className="contact-bottom">
                    <p className="contact-message">
                      {contact.last_message || 'No messages yet'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {/* Unclaimed / Tap to Claim / My Chat — matches AdminChatListScreen.js exactly */}
                      {!contact.admin_id && contact.status === 'open' && (
                        isStaff ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onClaimChat) onClaimChat(contact);
                            }}
                            style={{
                              backgroundColor: '#6F70FA',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Tap to Claim
                          </button>
                        ) : (
                          <span style={{
                            backgroundColor: '#FFF3E0',
                            color: '#FF9800',
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            whiteSpace: 'nowrap'
                          }}>
                            Unclaimed
                          </span>
                        )
                      )}
                      {contact.admin_id && contact.admin_id === myStaffId && contact.status === 'open' && (
                        <span style={{
                          backgroundColor: '#D1FAE5',
                          color: '#059669',
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          whiteSpace: 'nowrap'
                        }}>
                          My Chat
                        </span>
                      )}

                      {/* End button */}
                      {contact.status !== 'closed' && onEndChat && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEndChat(contact.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            backgroundColor: '#FEE2E2',
                            color: '#FF3B30',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '2px 6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="#FF3B30" strokeWidth="2"/>
                            <path d="M15 9l-6 6M9 9l6 6" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          End
                        </button>
                      )}
                      {contact.status === 'closed' && (
                        <span style={{
                          backgroundColor: '#E5E7EB',
                          color: '#6B7280',
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          whiteSpace: 'nowrap'
                        }}>
                          Ended
                        </span>
                      )}
                      {contact.unread_count > 0 && (
                        <span className="unread-badge">
                          {contact.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  {contactIsOnline && (
                    <div className="online-status">Online</div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-results">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="#ccc" strokeWidth="2"/>
              <path d="M21 21l-4.35-4.35" stroke="#ccc" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>No conversations found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
